<template>
	<el-dropdown trigger="click" placement="right-end" @visible-change="onVisibleChange">
		<div class="line-trigger" :title="'线路:' + activeName">
			<i class="el-icon-connection"></i>
			<span class="line-dot" :style="{ background: activeColor }"></span>
		</div>
		<el-dropdown-menu slot="dropdown" class="line-dropdown">
			<div class="line-head">
				<span>线路选择</span>
				<el-button type="text" size="mini" :loading="testing" @click.stop="runTest">
					{{ testing ? '测速中' : '重新测速' }}
				</el-button>
			</div>

			<div class="line-item" :class="{ active: mode === 'auto' }" @click="selectAuto">
				<i class="el-icon-magic-stick auto-icon"></i>
				<span class="li-name">自动(最优节点)</span>
				<i v-if="mode === 'auto'" class="el-icon-check check"></i>
			</div>

			<div class="line-split"></div>

			<div v-for="item in lines" :key="item.url" class="line-item"
				:class="{ active: mode === 'manual' && item.url === currentUrl }" @click="selectLine(item.url)">
				<span class="line-dot" :style="{ background: colorOf(item.rtt) }"></span>
				<span class="li-name">{{ nameOf(item.url) }}</span>
				<span class="li-rtt" :style="{ color: colorOf(item.rtt) }">{{ rttText(item.rtt) }}</span>
				<i v-if="mode === 'manual' && item.url === currentUrl" class="el-icon-check check"></i>
			</div>

			<div v-if="!lines.length" class="line-empty">无可用线路</div>
		</el-dropdown-menu>
	</el-dropdown>
</template>

<script>
import {
	getApiLines,
	getCurrentLine,
	setCurrentLine,
	applyLine,
	isManualSelected,
	isAbsoluteBase,
	pingAll,
	pickBest
} from '@/utils/apiLine'

export default {
	name: 'LineSelect',
	data() {
		return {
			lines: getApiLines().map((url) => ({ url, rtt: undefined })),
			mode: isManualSelected() ? 'manual' : 'auto',
			currentUrl: getCurrentLine(),
			absoluteBase: isAbsoluteBase(),
			testing: false,
			tested: false
		}
	},
	computed: {
		bestUrl() {
			return pickBest(this.lines.filter((l) => l.rtt !== undefined))
		},
		activeName() {
			const url = this.mode === 'manual' ? this.currentUrl : this.bestUrl || this.currentUrl
			return this.nameOf(url)
		},
		activeColor() {
			const url = this.mode === 'manual' ? this.currentUrl : this.bestUrl
			const found = this.lines.find((l) => l.url === url)
			return this.colorOf(found ? found.rtt : undefined)
		}
	},
	// 不在挂载时自动测速:线路池含多个域名,逐一探测会在控制台刷一片网络错误(不可达域名)。
	// 改为用户点开下拉时再测速(见 onVisibleChange)。默认走当前访问域名对应的 api 线路。
	methods: {
		nameOf(url) {
			try {
				// 兼容 api.xxx / htapi.xxx
				return new URL(url).host.replace(/^(api|htapi)\./, '')
			} catch (e) {
				return url || '默认'
			}
		},
		colorOf(rtt) {
			if (rtt === undefined) return '#909399' // 未测
			if (rtt === null) return '#c0c4cc' // 不可达
			if (rtt < 100) return '#67c23a' // 优
			if (rtt < 250) return '#e6a23c' // 中
			return '#f56c6c' // 差
		},
		rttText(rtt) {
			if (rtt === undefined) return '…'
			if (rtt === null) return '超时'
			return rtt + 'ms'
		},
		async runTest() {
			if (this.testing || !this.lines.length) return
			this.testing = true
			const prev = this.currentUrl
			const results = await pingAll(this.lines.map((l) => l.url))
			this.lines = results
			this.tested = true
			if (this.mode === 'auto') {
				const best = pickBest(results)
				if (best && this.absoluteBase) {
					applyLine(best)
					this.currentUrl = best
					// 自动选优变更了生效线路:WS 也要同步切到新线路,避免 HTTP/WS 不一致
					if (best !== prev) this.notifyLineSwitched()
				}
			}
			this.testing = false
		},
		onVisibleChange(visible) {
			if (visible && !this.tested && !this.testing) this.runTest()
		},
		selectAuto() {
			if (this.mode === 'auto') return // 已是自动,无需重复切换
			this.mode = 'auto'
			setCurrentLine(null)
			const best = pickBest(this.lines)
			if (best && this.absoluteBase) {
				applyLine(best)
				this.currentUrl = best
				this.$message.success('已切换为自动选择最优线路:' + this.nameOf(best))
			} else {
				applyLine(null)
				this.$message.success('已恢复自动线路')
			}
			this.notifyLineSwitched()
		},
		selectLine(url) {
			if (this.mode === 'manual' && this.currentUrl === url) return // 已选中该线路
			this.mode = 'manual'
			this.currentUrl = url
			setCurrentLine(url)
			applyLine(url)
			this.$message.success('已切换线路:' + this.nameOf(url))
			this.notifyLineSwitched()
		},
		// 通知全局:线路已变更。HTTP 下个请求自动走新线路;WS 由 Home 监听后切到新线路连接
		notifyLineSwitched() {
			this.$eventBus.$emit('switchLine')
		}
	}
}
</script>

<style lang="scss" scoped>
.line-trigger {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: #fff;
	position: relative;

	i.el-icon-connection {
		font-size: 20px;
	}

	.line-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		margin-top: 3px;
		display: inline-block;
	}
}
</style>

<style lang="scss">
.line-dropdown {
	min-width: 240px;
	padding: 6px 0;

	.line-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 4px 14px 8px;
		font-size: 12px;
		color: #909399;
		border-bottom: 1px solid #f0f0f0;
	}

	.line-split {
		height: 1px;
		background: #f0f0f0;
		margin: 4px 0;
	}

	.line-item {
		display: flex;
		align-items: center;
		padding: 7px 14px;
		font-size: 13px;
		color: #303133;
		cursor: pointer;

		&:hover {
			background: #f5f7fa;
		}

		&.active {
			color: #409eff;
			background: #ecf5ff;
		}

		.auto-icon {
			margin-right: 8px;
			color: #409eff;
		}

		.line-dot {
			width: 8px;
			height: 8px;
			border-radius: 50%;
			margin-right: 8px;
			flex: 0 0 auto;
		}

		.li-name {
			flex: 1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.li-rtt {
			margin-left: 10px;
			font-size: 12px;
			font-variant-numeric: tabular-nums;
		}

		.check {
			margin-left: 8px;
			color: #409eff;
		}
	}

	.line-empty {
		padding: 12px 14px;
		color: #c0c4cc;
		font-size: 13px;
		text-align: center;
	}
}
</style>
